// Action Creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Controls
import { ContentSensitivityLabel } from "@ms/planner-presentational-controls/lib/local/contentSensitivityLabel/ContentSensitivityLabel";
// Hooks
import { useActionCreators } from "../../hooks/useActionCreators";
import { useConfigProvider } from "../../hooks/useConfigProvider";
import { useStores } from "../../hooks/useStores";
// Models
import { ContainerType, SensitivityLabelActionSource, SensitivityLabelAssignmentMethod } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
// Stores
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Utilities
import * as React from "react";
/**
 * Smart container component that connects the ContentSensitivityLabel presentational component
 * to the Flux store and action creators.
 */ export const ContentSensitivityLabelContainer = ({ planId })=>{
    const configProvider = useConfigProvider();
    const [planStore, tenantStore, appContextStore] = useStores([
        Store.Plan,
        Store.Tenant,
        Store.AppContext
    ]);
    const [planActionCreator] = useActionCreators([
        ActionCreator.Plan
    ]);
    const currentPlan = planStore.getPlan(planId).data;
    const isGuestUser = appContextStore.getAppContext().isGuestUser;
    if (!currentPlan || isGuestUser) {
        return null;
    }
    const isContentSensitivityEnabledForContainerType = (planContainerType)=>{
        const contentLevelSensitivityLabelsContainerType = configProvider().settings.contentLevelSensitivityLabelsContainerType;
        // Validate that containerType is a valid ContainerType enum value
        if (!Object.values(ContainerType).includes(planContainerType)) {
            return false;
        }
        // Validate that containerType is included in the configured container types
        return contentLevelSensitivityLabelsContainerType.includes(planContainerType);
    };
    const isContentSensitivityLabelEnabled = configProvider().flights.EnableContentLevelSensitivityLabelsEvo && isContentSensitivityEnabledForContainerType(currentPlan.container.containerType);
    if (!isContentSensitivityLabelEnabled) {
        return null;
    }
    const currentLabelId = currentPlan.contentSensitivityLabelAssignment?.sensitivityLabelId ?? null;
    const planContainerType = currentPlan.container.containerType;
    const isRosterOrOnlineMeetingContainer = planContainerType === ContainerType.Roster || planContainerType === ContainerType.OnlineMeeting;
    // Get available labels and policy settings from tenant store
    const fileSensitivityLabels = tenantStore.getCurrentFileSensitivityLabels().data ?? [];
    const fileSensitivityPolicySettings = tenantStore.getCurrentFileSensitivityPolicySettings().data ?? null;
    /**
     * Handles the sensitivity label change event
     * @param newLabelId The new sensitivity label ID to apply
     * @param justification Optional justification for the label change (required for downgrades)
     */ const handleLabelChange = (newLabelId, justification)=>{
        const updatedPlan = currentPlan.setProperty("contentSensitivityLabelAssignment", {
            sensitivityLabelId: newLabelId,
            assignmentMethod: SensitivityLabelAssignmentMethod.Privileged,
            justificationText: justification ?? null,
            actionSource: SensitivityLabelActionSource.Manual
        });
        planActionCreator.updatePlan(currentPlan, updatedPlan);
    };
    return /*#__PURE__*/ React.createElement(ContentSensitivityLabel, {
        currentLabelId: currentLabelId,
        fileSensitivityLabels: fileSensitivityLabels,
        fileSensitivityPolicySettings: fileSensitivityPolicySettings,
        disabled: isRosterOrOnlineMeetingContainer,
        onLabelChange: handleLabelChange
    });
};
